export interface CountryProfile {
  id: string;
  flag: string;
  name: string;
  cities: string;
  image: string;
  imageWidth: number;
  imageHeight: number;
  imageSeq: string;
  imageQuery: string;
  description: string;
  tags: string[];
  bestFor: string;
  isPremium?: boolean;
}

export interface ComparisonRow {
  factor: string;
  hungary: string;
  turkey: string;
  poland: string;
  spain: string;
  hungaryStatus?: "check" | "partial" | "neutral";
  turkeyStatus?: "check" | "partial" | "neutral";
  polandStatus?: "check" | "partial" | "neutral";
  spainStatus?: "check" | "partial" | "neutral";
}

export interface KeyQuestion {
  icon: string;
  question: string;
  answer: string;
}

export const countryProfiles: CountryProfile[] = [
  {
    id: "hungary",
    flag: "HU",
    name: "Hungary",
    cities: "Budapest",
    image: "https://readdy.ai/api/search-image?query=Budapest%20Parliament%20building%20by%20the%20Danube%20river%20at%20sunset%2C%20dramatic%20golden%20light%20reflecting%20on%20water%2C%20European%20architectural%20landmark%2C%20editorial%20travel%20photography%2C%20high%20detail&width=800&height=360&seq=country-hungary-01&orientation=landscape",
    imageWidth: 800,
    imageHeight: 360,
    imageSeq: "country-hungary-01",
    imageQuery: "Budapest Parliament building by the Danube river at sunset, dramatic golden light reflecting on water, European architectural landmark, editorial travel photography, high detail",
    description:
      "Hungary has been Europe's dental tourism capital for over 30 years. Budapest clinics are internationally renowned for implantology and full-arch reconstructions. EU-regulated, CBCT-equipped, and experienced with complex cases. Treatment typically requires two trips (placement + final restoration). Costs are 50–70 % below UK private prices for comparable quality.",
    tags: ["Implants", "Full Arch", "Long-term", "EU-regulated"],
    bestFor:
      "Patients planning implants or full-arch restorations who prioritise long-term outcomes and can manage two trips.",
  },
  {
    id: "turkey",
    flag: "TR",
    name: "Turkey",
    cities: "Istanbul / Antalya",
    image: "https://readdy.ai/api/search-image?query=Istanbul%20skyline%20with%20Blue%20Mosque%20and%20Bosphorus%20at%20golden%20hour%2C%20warm%20amber%20light%2C%20minarets%20silhouette%2C%20cityscape%20panorama%2C%20editorial%20travel%20photography%2C%20high%20detail&width=800&height=360&seq=country-turkey-01&orientation=landscape",
    imageWidth: 800,
    imageHeight: 360,
    imageSeq: "country-turkey-01",
    imageQuery: "Istanbul skyline with Blue Mosque and Bosphorus at golden hour, warm amber light, minarets silhouette, cityscape panorama, editorial travel photography, high detail",
    description:
      "Turkey is the most price-competitive destination and a global hub for cosmetic dentistry, particularly veneers. Most treatment can be completed in a single short trip (5–7 days). Quality varies considerably between clinics, making due diligence especially important. Not subject to EU dental regulations, but many clinics are accredited by international bodies.",
    tags: ["Veneers", "Cosmetic", "One Trip", "Budget-led"],
    bestFor:
      "Patients seeking cosmetic work (veneers, crowns) at the lowest available price, who accept higher variability and have done thorough research.",
  },
  {
    id: "poland",
    flag: "PL",
    name: "Poland",
    cities: "Warsaw / Krakow",
    image: "https://readdy.ai/api/search-image?query=Warsaw%20old%20town%20square%20with%20colourful%20historic%20buildings%2C%20market%20square%20fountain%2C%20clear%20blue%20sky%2C%20European%20architecture%2C%20editorial%20travel%20photography%2C%20bright%20natural%20daylight&width=800&height=360&seq=country-poland-01&orientation=landscape",
    imageWidth: 800,
    imageHeight: 360,
    imageSeq: "country-poland-01",
    imageQuery: "Warsaw old town square with colourful historic buildings, market square fountain, clear blue sky, European architecture, editorial travel photography, bright natural daylight",
    description:
      "Poland offers an excellent balance: EU-standard care, transparent pricing, and shorter travel from the UK. Increasingly popular for implants, crowns, bridges, and general restorative work. English-speaking clinics are common in major cities. Pricing is typically 45–65 % below UK private rates. A strong all-rounder option for cautious travellers.",
    tags: ["Crowns & Bridges", "Implants", "EU Standards", "Balanced"],
    bestFor:
      "Patients who want EU-regulated care, transparent pricing, and flexible scheduling across a broad range of treatments.",
  },
  {
    id: "spain",
    flag: "ES",
    name: "Spain",
    cities: "Barcelona / Madrid / Costa del Sol",
    image: "https://readdy.ai/api/search-image?query=Barcelona%20cityscape%20with%20Sagrada%20Familia%20and%20Mediterranean%20skyline%2C%20warm%20golden%20afternoon%20light%2C%20Gaudi%20architecture%2C%20coastal%20city%20panorama%2C%20editorial%20travel%20photography%2C%20high%20detail&width=800&height=360&seq=country-spain-01&orientation=landscape",
    imageWidth: 800,
    imageHeight: 360,
    imageSeq: "country-spain-01",
    imageQuery: "Barcelona cityscape with Sagrada Familia and Mediterranean skyline, warm golden afternoon light, Gaudi architecture, coastal city panorama, editorial travel photography, high detail",
    description:
      "Spain is our premium EU-regulated destination — combining full EU clinical standards with some of the easiest travel from the UK (2–3 hour flights, no long-haul). Spanish dental clinics in major cities and coastal areas are internationally accredited, well-equipped, and increasingly experienced with UK patients. The proximity makes aftercare and follow-up visits far more practical than any other destination. Prices are higher than Hungary or Poland but significantly below UK private rates.",
    tags: ["EU-regulated", "Easy Travel", "Strong Aftercare", "Premium Quality"],
    bestFor:
      "Patients who want premium EU-regulated care, straightforward travel, and the ability to return easily for aftercare or follow-up visits.",
    isPremium: true,
  },
];

export const comparisonRows: ComparisonRow[] = [
  {
    factor: "Regulatory framework",
    hungary: "EU Directive",
    turkey: "Non-EU (varies)",
    poland: "EU Directive",
    spain: "EU Directive",
    hungaryStatus: "check",
    turkeyStatus: "partial",
    polandStatus: "check",
    spainStatus: "check",
  },
  {
    factor: "Typical trips required",
    hungary: "2 trips (1–4 weeks apart)",
    turkey: "1 trip (5–7 days)",
    poland: "1–2 trips (flexible)",
    spain: "1–2 trips (easy return)",
  },
  {
    factor: "Implant savings vs UK private",
    hungary: "50–70 %",
    turkey: "55–75 %",
    poland: "45–65 %",
    spain: "30–50 %",
  },
  {
    factor: "Veneer savings vs UK private",
    hungary: "40–60 %",
    turkey: "60–80 %",
    poland: "40–60 %",
    spain: "30–50 %",
  },
  {
    factor: "Full arch (All-on-4) approx. cost",
    hungary: "£7,000–£12,000",
    turkey: "£5,000–£9,000",
    poland: "£7,500–£12,000",
    spain: "£9,000–£14,000",
  },
  {
    factor: "Single implant approx. cost",
    hungary: "£900–£1,400",
    turkey: "£700–£1,100",
    poland: "£900–£1,350",
    spain: "£1,100–£1,600",
  },
  {
    factor: "Veneer per tooth approx. cost",
    hungary: "£250–£400",
    turkey: "£180–£350",
    poland: "£230–£380",
    spain: "£300–£450",
  },
  {
    factor: "Quality consistency",
    hungary: "High",
    turkey: "Variable",
    poland: "High",
    spain: "High",
    hungaryStatus: "check",
    turkeyStatus: "partial",
    polandStatus: "check",
    spainStatus: "check",
  },
  {
    factor: "English-speaking clinics",
    hungary: "Common",
    turkey: "Common",
    poland: "Common",
    spain: "Common",
    hungaryStatus: "check",
    turkeyStatus: "check",
    polandStatus: "check",
    spainStatus: "check",
  },
  {
    factor: "Implant brand quality",
    hungary: "Nobel, Straumann, Osstem",
    turkey: "Varies (confirm)",
    poland: "Nobel, Straumann, Osstem",
    spain: "Nobel, Straumann, Zimmer",
  },
  {
    factor: "In-house dental lab",
    hungary: "Common",
    turkey: "Sometimes",
    poland: "Common",
    spain: "Common",
    hungaryStatus: "check",
    turkeyStatus: "partial",
    polandStatus: "check",
    spainStatus: "check",
  },
  {
    factor: "Written guarantee typical?",
    hungary: "Yes",
    turkey: "Sometimes",
    poland: "Yes",
    spain: "Yes",
    hungaryStatus: "check",
    turkeyStatus: "partial",
    polandStatus: "check",
    spainStatus: "check",
  },
  {
    factor: "Flight time from UK",
    hungary: "~2.5 hrs",
    turkey: "~3.5–4.5 hrs",
    poland: "~2–2.5 hrs",
    spain: "~2–3 hrs",
  },
  {
    factor: "Best suited for",
    hungary: "Implants, full arch",
    turkey: "Veneers, cosmetics",
    poland: "All treatments",
    spain: "Premium care, easy aftercare",
  },
  {
    factor: "Risk level (with research)",
    hungary: "Low–Medium",
    turkey: "Medium–High",
    poland: "Low–Medium",
    spain: "Low",
    hungaryStatus: "check",
    turkeyStatus: "partial",
    polandStatus: "check",
    spainStatus: "check",
  },
];

export const keyQuestions: KeyQuestion[] = [
  {
    icon: "ri-magic-line",
    question: "What treatment do you need?",
    answer:
      "Implants and full-arch work — Hungary or Poland. Veneers and cosmetic work — Turkey or Hungary. Crowns and bridges — any destination. Premium care with easy follow-up — Spain.",
  },
  {
    icon: "ri-money-pound-circle-line",
    question: "What is your budget priority?",
    answer:
      "Lowest absolute price — Turkey. Best value with EU safety standards — Poland. Willing to invest more for the most established track record — Hungary. Premium EU care with minimal travel friction — Spain.",
  },
  {
    icon: "ri-plane-line",
    question: "How many trips can you manage?",
    answer:
      "One short trip only — Turkey (most treatments in 5–7 days). Two trips for best implant results — Hungary. Flexible and close to home — Poland. Easy return visits for aftercare — Spain (2–3 hr flights, multiple daily routes).",
  },
  {
    icon: "ri-shield-check-line",
    question: "How important is regulatory protection?",
    answer:
      "EU consumer and clinical regulations apply in Hungary, Poland, and Spain. Turkey operates outside EU frameworks — quality can be excellent, but due diligence is more important.",
  },
  {
    icon: "ri-file-list-3-line",
    question: "Do you want a written guarantee?",
    answer:
      "Written guarantees are standard practice in reputable Hungarian, Polish, and Spanish clinics. In Turkey they exist but vary — always get specifics in writing before committing.",
  },
  {
    icon: "ri-search-line",
    question: "How much research time do you have?",
    answer:
      "Less time to vet clinics — stick with Hungary, Poland, or Spain where quality is more consistent. If you enjoy thorough research and can identify the best Turkey clinics — the savings can be significant.",
  },
  {
    icon: "ri-hospital-line",
    question: "How important is aftercare access?",
    answer:
      "If being able to return for a follow-up or managing a complication without a long-haul flight matters to you, Spain is the clear answer. Multiple daily flights from UK airports, short journey times, and no time zone difference make it uniquely practical for ongoing care.",
  },
];